import React, {
  createContext,
  CSSProperties,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Chrome that must NOT travel with the carousel.
 *
 * Swiping between tables slides the table surface — the felt, the players,
 * cards and chips on it, and the bottom bar (chat / hand history / action
 * buttons), all of which belong to one specific table. The controls along the
 * TOP are a different thing: the hamburger, Add Chips, support and the
 * multi-table navigation strip are global, and even the two that are
 * table-dependent (Sit Out / Exit Table) read as fixed furniture rather than
 * part of the table you are dragging. Sliding them looks like the whole app is
 * moving instead of the table.
 *
 * They can't simply be hoisted out of the per-table subtree: they read that
 * table's state (Sit Out / Exit visibility, Add Chips eligibility) and its
 * GameContext (opening the Add Chips modal, which lives in the same subtree).
 * So they stay in the React tree exactly where they are — context and all — and
 * only their DOM is portalled into a stationary host outside the moving track.
 *
 * Mid-slide, two tables are mounted at once. Only the ACTIVE one supplies the
 * fixed chrome, so the header never renders twice, and it shows the state of
 * the table being moved to.
 */

export type FixedChromeValue = {
  /** Stationary element outside the carousel; null when there is no carousel. */
  host: HTMLElement | null;
  /** True for the panel showing the table the player is on. */
  isActive: boolean;
};

const FixedChromeContext = createContext<FixedChromeValue>({ host: null, isActive: true });

export const FixedChromeProvider = FixedChromeContext.Provider;

/**
 * How many painted frames the OUTGOING chrome stays mounted (inert, under the
 * incoming copy) after losing active status. Replacing the whole header DOM at
 * a swipe commit invalidates its raster, and under a heavy commit the fresh
 * content can miss the frame's raster deadline - painted as a fully BLANK
 * header for one frame (caught on a device screencast: settled-with-header ->
 * mid-slide-with-NO-header -> mid-slide-with-header). Keeping the old pixels
 * underneath means a raster-lag frame shows the previous header instead of
 * nothing. ONE frame matches the observed gap exactly.
 *
 * The cover frame must never be a frame where BOTH copies paint. Every
 * surface in this chrome is translucent - rgba(255,255,255,0.05) on the
 * header buttons, rgba(0,0,0,0.6) on the dark pills, 0.32-alpha borders, an
 * 0.85-opacity logo - so two stacked copies composite each one twice. The
 * light surfaces go 0.05 -> 0.0975 (brighter), the dark ones 0.6 -> 0.84
 * (darker), and the borders gain saturation: the whole top bar visibly
 * shifts, in different directions per element, for exactly as long as the
 * copies overlap. That is the flash reported on every table switch (Jason
 * 2026-08-24), and it is why this count came down from 2 to 1 (JRam
 * eb8d13e216) without ever going away.
 *
 * So the incoming copy HOLDS ITS PAINT for the same window (see holdFrames
 * below): the outgoing copy keeps covering the swap exactly as before, the
 * incoming one is mounted and laid out underneath it but does not paint, and
 * they trade places in a single commit. One painted copy at all times means
 * no double-composite, and the incoming chrome gets a full frame of layout
 * before it has to raster. The stale copy cannot be tapped (inert).
 */
const LAME_DUCK_FRAMES = 1;

// Wall-clock backstop for the two rAF handovers below. requestAnimationFrame
// does not fire in a backgrounded tab, so without this a tab-away mid-swipe
// can strand the chrome hidden (see the failsafes for the full scenario).
// Matches SlideContainer's CUT_STAGE_FAILSAFE_MS — far longer than the one or
// two frames these loops need, short enough that a stuck handover self-heals
// before the player can act on it.
const CHROME_HOLD_FAILSAFE_MS = 250;

/**
 * Renders its children into the stationary host instead of in place.
 *
 * Falls back to rendering inline wherever there is no host — the multi-table
 * grid, storybook, tests — so this is a no-op outside the single-table
 * carousel.
 *
 * The wrapper div is ALWAYS present (never added or removed on an active
 * transition): re-parenting the header's DOM nodes would invalidate their
 * raster, which is the exact one-frame blank this component now exists to
 * prevent. Only attributes change between active and lame-duck.
 */
export const FixedChrome: React.FC<PropsWithChildren> = ({ children }) => {
  const { host, isActive } = useContext(FixedChromeContext);
  // 'active' -> shown; 'lame' -> just lost active status, kept mounted for
  // LAME_DUCK_FRAMES; 'off' -> unmounted. Transitions are applied DURING
  // RENDER (not in an effect) so the lame-duck copy survives the very commit
  // that removes active status — an effect runs after that commit has painted,
  // which is one frame too late.
  const [phase, setPhase] = useState<'active' | 'lame' | 'off'>(isActive ? 'active' : 'off');
  // Frames a newly-active copy waits before it paints, so it never composites
  // on top of the outgoing copy still covering the swap. Zero on first mount
  // (nothing to hand over from) and zero once the handover is done.
  const [holdFrames, setHoldFrames] = useState(0);
  if (isActive && phase !== 'active') {
    setPhase('active');
    setHoldFrames(LAME_DUCK_FRAMES);
  }
  if (!isActive && phase === 'active') setPhase('lame');

  useEffect(() => {
    if (phase !== 'lame') return undefined;
    let frames = 0;
    let id = requestAnimationFrame(function step() {
      frames += 1;
      if (frames >= LAME_DUCK_FRAMES) {
        setPhase((p) => (p === 'lame' ? 'off' : p));
      } else {
        id = requestAnimationFrame(step);
      }
    });
    // Same backstop, same reason: this loop is the only thing that retires the
    // outgoing copy, and a backgrounded tab freezes it mid-handover.
    const failsafe = setTimeout(
      () => setPhase((p) => (p === 'lame' ? 'off' : p)),
      CHROME_HOLD_FAILSAFE_MS
    );
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(failsafe);
    };
  }, [phase]);

  // Counts the incoming copy in over the SAME number of frames the outgoing
  // one stays up. Both timers are armed by the same isActive flip, in the same
  // commit, so their rAF callbacks land in the same frame and React batches
  // the two state updates into one commit: the old copy stops painting in the
  // very frame the new one starts. No frame with two, none with neither.
  useEffect(() => {
    if (holdFrames <= 0) return undefined;
    let frames = 0;
    let id = requestAnimationFrame(function step() {
      frames += 1;
      if (frames >= holdFrames) setHoldFrames(0);
      else id = requestAnimationFrame(step);
    });
    // FAILSAFE. requestAnimationFrame does not fire in a backgrounded tab, and
    // what this window hides is the LIVE chrome: swipe to another table (which
    // arms the hold), switch browser tabs before the frame runs, come back, and
    // the incoming copy is still visibility:hidden while the outgoing one is
    // already inert and pointer-events:none. No top chrome painted and none
    // tappable — no Lobby button, no cashier, no hamburger — until something
    // else flips isActive. SlideContainer hit the identical hazard on the
    // staged cut and answered it with CUT_STAGE_FAILSAFE_MS; same answer here
    // (JRam review, #3721).
    const failsafe = setTimeout(() => setHoldFrames(0), CHROME_HOLD_FAILSAFE_MS);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(failsafe);
    };
  }, [holdFrames]);

  if (!host) return <>{children}</>;
  if (!isActive && phase !== 'lame') return null;

  const active = isActive;
  const wrapperStyle: CSSProperties = {
    // Same box as the host (which is itself inset:0 over the panel), so the
    // absolutely-positioned clusters inside resolve exactly as they did when
    // they were direct children of the host.
    position: 'absolute',
    inset: 0,
    // The host layer is pass-through; keep the wrapper pass-through too and
    // let each cluster re-enable its own pointer events (their existing
    // contract - see FixedChrome.pointerEvents.test).
    pointerEvents: 'none',
    // The incoming header covers the lame-duck one whatever their DOM order
    // (a swipe BACK re-activates a copy that sits EARLIER in the host).
    zIndex: active ? 1 : 0,
    // Mounted and laid out, but not yet painted — the outgoing copy is still
    // on screen and two translucent copies would double-composite (see the
    // LAME_DUCK_FRAMES docblock).
    visibility: active && holdFrames > 0 ? 'hidden' : undefined
  };
  const inertProps = active ? {} : ({ inert: '' } as Record<string, string>);
  return createPortal(
    <div style={wrapperStyle} aria-hidden={active ? undefined : true} {...inertProps}>
      {children}
    </div>,
    host
  );
};

export default FixedChromeContext;
